/**
 * Application root.
 *
 * Directive §81: the server is authoritative. This component renders authorized
 * server state and holds no authoritative records of its own.
 *
 * Directive §62/§81: no localStorage, sessionStorage, IndexedDB or cookie
 * writing happens anywhere in the client. The session is an HttpOnly cookie the
 * browser manages; JavaScript cannot read it and does not try.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AppShell, type ScreenId, type SaveState } from './components/AppShell.js';
import { HomeScreen, type HomeData } from './screens/Home.js';
import { PeopleScreen, type PeopleFilter, type PersonSummary } from './screens/People.js';
import { VisitsScreen, type VisitTab, type VisitsData } from './screens/Visits.js';
import { FieldModeScreen, FieldOutcomeBody, type FieldStop, type FieldOutcomeId }
  from './screens/FieldMode.js';
import { TripWizard, type WizardStep, type SelectableVisit, type SelectableTeam,
  type PlanReview } from './screens/TripWizard.js';
import { Sheet, Toast } from './components/Sheet.js';
import { PersonScreen, CallLogBody, ScheduleVisitBody, callLogDefaults,
  scheduleVisitDefaults, type PersonDetail, type CallLogState,
  type ScheduleVisitState } from './screens/Person.js';
import { TeamRouteScreen, type TeamRouteData } from './screens/TeamRoute.js';
import { MapScreen, type MapData } from './screens/Map.js';
import { AdminScreen, UserDetailBody, type AdminTab, type AdminData, type AdminUser }
  from './screens/Admin.js';
import { SCOPE_LABELS, type ScopeKind } from './screens/adminTypes.js';
import {
  SignInScreen, MfaPromptScreen, ForgotPasswordScreen, PasswordResetScreen,
  FirstSignInPasswordScreen, FirstSignInTotpScreen, FirstSignInSecurityKeyScreen,
  FirstSignInRecoveryCodesScreen, type MfaMethod, type FirstSignInStep,
  type PasswordPolicyView, MobileActivationScreen,
} from './screens/Auth.js';
import {
  ImportScreen, LocationsReviewScreen, CalendarScreen, MoreScreen,
  ProfileScreen, HelpScreen, type ImportPreview, type UnresolvedLocation,
  type CalendarView, type CalendarEventView, type CalendarConnectionView,
  type MoreLink, type ProfileData,
  type HelpTopic,
} from './screens/Workspace.js';
import { api, ApiError } from './lib/api.js';
import { TeamAssignmentsScreen } from './screens/TeamAssignments.js';
import { usePresence } from './hooks/usePresence.js';
import { CallCenterScreen, type CallCenterContact, type CallCenterData } from './screens/CallCenter.js';
import { FieldWellnessScreen } from './screens/FieldWellnessWorkspace.js';
import { FieldWorkScreen } from './screens/FieldWork.js';
import { isNativeMobile } from './mobile/platform.js';

type CreateUserValues = { displayName: string; email: string; role: string };
type CreateUserErrors = Partial<Record<keyof CreateUserValues | 'form', string>>;

export function adminAccessCopy(reason?: string): string {
  if (reason === 'WEBAUTHN_REQUIRED') {
    return 'Verify with the security key registered to this account to open this protected area.';
  }
  if (reason === 'STEP_UP_REQUIRED') {
    return 'Your privileged session expired. Verify with your security key to continue.';
  }
  if (reason === 'PERMISSION_NOT_GRANTED' || reason === 'SCOPE_MISMATCH'
      || reason === 'CALL_CENTER_MANAGEMENT_NOT_GRANTED') {
    return 'Your account does not have permission to open this administration area.';
  }
  return 'This administration area is protected and could not be opened.';
}

export function App() {
  const [screen, setScreen] = useState<ScreenId>(() => {
    if (typeof window === 'undefined') return 'home';
    if (isNativeMobile()) return 'call-center';
    return new URLSearchParams(window.location.search).get('app') === 'call-center'
      ? 'call-center' : 'home';
  });
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [me, setMe] = useState<{
    userId: string; displayName: string; initials: string; roles: string[];
  } | null>(null);

  const [home, setHome] = useState<HomeData | null>(null);
  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [peopleTotal, setPeopleTotal] = useState<number | null>(null);
  const [peopleFilter, setPeopleFilter] = useState<PeopleFilter>('all');
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleLoadingMore, setPeopleLoadingMore] = useState(false);
  const peopleRequest = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [visitsData, setVisitsData] = useState<VisitsData | null>(null);
  const [visitTab, setVisitTab] = useState<VisitTab>('today');
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitsLoadingMore, setVisitsLoadingMore] = useState(false);
  const [visitsError, setVisitsError] = useState<{
    message: string; recoverable: boolean;
  } | null>(null);
  const [visitsRetry, setVisitsRetry] = useState(0);
  const visitsRequest = useRef(0);
  const visitsScroll = useRef(0);
  const visitsOpenedPerson = useRef(false);

  const [fieldStop, setFieldStop] = useState<FieldStop | null>(null);
  const [fieldPlanId, setFieldPlanId] = useState<string | null>(null);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [outcome, setOutcome] = useState<FieldOutcomeId | null>(null);
  const [outcomeNote, setOutcomeNote] = useState('');

  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [wizardVisits, setWizardVisits] = useState<SelectableVisit[]>([]);
  const [wizardTeams, setWizardTeams] = useState<SelectableTeam[]>([]);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [tripDate, setTripDate] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [review, setReview] = useState<PlanReview | null>(null);
  const [planning, setPlanning] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);

  const [person, setPerson] = useState<PersonDetail | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [callLogOpen, setCallLogOpen] = useState(false);
  const [callLog, setCallLog] = useState<CallLogState>(callLogDefaults);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [avAddressOpen, setAvAddressOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTeamId, setReassignTeamId] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [avAddress, setAvAddress] = useState('');
  const [avNotes, setAvNotes] = useState('');
  const [avTasks, setAvTasks] = useState<Awaited<ReturnType<typeof api.avTasks>>['items']>([]);
  const [avUnreadCount, setAvUnreadCount] = useState(0);
  const [avSelectedTaskId, setAvSelectedTaskId] = useState<string | null>(null);
  const [avDecision, setAvDecision] = useState('research_required');
  const [avDecisionNotes, setAvDecisionNotes] = useState('');
  const [avValidatedAddress, setAvValidatedAddress] = useState('');
  const [avCity, setAvCity] = useState('');
  const [avState, setAvState] = useState('');
  const [avZip, setAvZip] = useState('');
  const [avLat, setAvLat] = useState('');
  const [avLng, setAvLng] = useState('');
  useEffect(() => {
    if (!me?.roles.includes('AV')) { setAvUnreadCount(0); return; }
    const refresh = () => void api.avNotificationCount()
      .then((result) => setAvUnreadCount(result.unread)).catch(() => undefined);
    refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(timer);
  }, [me?.userId, me?.roles]);

  const [schedule, setSchedule] = useState<ScheduleVisitState>(() =>
    scheduleVisitDefaults(new Date().toISOString().slice(0, 10)));
  const [callCenterContact, setCallCenterContact] = useState<CallCenterContact | null>(null);
  const [callCenterConnectRequest, setCallCenterConnectRequest] = useState(0);
  const [teamOptions, setTeamOptions] = useState<Array<{ id: string; name: string }>>([]);

  const [teamRoute, setTeamRoute] = useState<TeamRouteData | null>(null);
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [mapTeam, setMapTeam] = useState<string | 'all'>('all');

  const [adminTab, setAdminTab] = useState<AdminTab>('users');
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [adminAccessMessage, setAdminAccessMessage] = useState<string | null>(null);
  const [adminAccessReason, setAdminAccessReason] = useState<string | null>(null);
  const [adminAccessBusy, setAdminAccessBusy] = useState(false);
  const [adminAccessError, setAdminAccessError] = useState<string | null>(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createUser, setCreateUser] = useState<CreateUserValues>({
    displayName: '', email: '', role: 'Administrator',
  });
  const [createUserErrors, setCreateUserErrors] = useState<CreateUserErrors>({});
  const [createUserSubmitting, setCreateUserSubmitting] = useState(false);
  const [invitationSending, setInvitationSending] = useState(false);
  const [adminStepUpOpen, setAdminStepUpOpen] = useState(false);
  const [adminStepUpBusy, setAdminStepUpBusy] = useState(false);
  const [adminStepUpError, setAdminStepUpError] = useState<string | null>(null);
  const [adminStepUpOptions, setAdminStepUpOptions] = useState<unknown>(null);
  const [pendingAdminChange, setPendingAdminChange] = useState<{
    userId: string; kind: 'role' | 'scope'; value: string;
  } | null>(null);

  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [importing, setImporting] = useState(false);
  const [unresolved, setUnresolved] = useState<UnresolvedLocation[]>([]);
  const [locationEditor, setLocationEditor] = useState<{
    id: string; mode: 'edit' | 'place'; address: string;
    city: string; state: string; zip: string; lat: string; lng: string;
  } | null>(null);
  const [calendarView, setCalendarView] = useState<CalendarView>('Agenda');
  const [calendarDate, setCalendarDate] = useState(() => dateKeyInTimeZone(new Date(), 'America/Phoenix'));
  const [calendar, setCalendar] = useState<{
    periodLabel: string;
    events: CalendarEventView[];
    connections: CalendarConnectionView[];
    disclosureStatement: string;
    timeZone: string;
  } | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<{
    message: string; recoverable: boolean;
  } | null>(null);
  const [calendarRetry, setCalendarRetry] = useState(0);
  const calendarRequest = useRef(0);
  const calendarScroll = useRef(0);
  const calendarOpenedPerson = useRef(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [helpTopics, setHelpTopics] = useState<HelpTopic[]>([]);
  const [callCenter, setCallCenter] = useState<CallCenterData | null>(null);

  const [setupToken] = useState<string | null>(() => takeSetupTokenFromLocation());
  const [authStage, setAuthStage] = useState<
    'sign_in' | 'mfa' | 'forgot' | 'reset' | 'first_sign_in'>(
      () => setupToken ? 'reset' : 'sign_in',
    );
  const [mfaMethod, setMfaMethod] = useState<MfaMethod>('totp');
  const [firstStep, setFirstStep] = useState<FirstSignInStep>('password');
  const [authError, setAuthError] = useState<string | null>(null);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicyView | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [forgotSent, setForgotSent] = useState(false);
  const [mfaAvailable, setMfaAvailable] = useState<MfaMethod[]>(['webauthn', 'totp']);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [localSignInEnabled, setLocalSignInEnabled] = useState(true);
  const [totpSetup, setTotpSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [mePermissions, setMePermissions] = useState<string[]>([]);
  const [mobileActivationBusy, setMobileActivationBusy] = useState(false);
  const [mobileActivationError, setMobileActivationError] = useState<string | null>(null);

  /** §5 More menu. Admin is hidden without the permission — courtesy only. §35 */
  const MORE_LINKS: MoreLink[] = [
    { id: 'profile', label: 'Profile', description: 'Security, access and sessions', group: 'My Account' },
    { id: 'notifications', label: avUnreadCount > 0
      ? `Notifications (${avUnreadCount} new)` : 'Notifications',
      description: 'Updates that need your attention', group: 'My Account' },
    { id: 'sign-out', label: 'Sign Out', description: 'End this session securely', group: 'My Account' },
    { id: 'help', label: 'Help', description: 'Guidance for using VERITAS', group: 'Support' },
    { id: 'call-operations', label: 'Call Operations',
      description: 'Live call-center operations and diagnostics', group: 'Support',
      requiresPermission: 'security.manage' },
    { id: 'report-problem', label: 'Report a Problem',
      description: 'Get help with an operational or technical issue', group: 'Support' },
    ...(me?.roles.some((role) => role === 'Manager' || role === 'Administrator' || role === 'Super Administrator')
      ? [
        { id: 'teams', label: 'Teams', description: 'Team setup and coverage', group: 'Management' as const },
        { id: 'users-access', label: 'Users & Access', description: 'Accounts, roles and permissions',
          group: 'Management' as const, requiresPermission: 'users.read' },
        { id: 'route-settings', label: 'Route Settings',
          description: 'Planning rules, capacity and route controls', group: 'Management' as const },
        { id: 'audit-history', label: 'Audit History', description: 'Review protected activity records',
          group: 'Management' as const, requiresPermission: 'audit.read' },
      ] : []),
  ];

  // Session identity. A 401 means the cookie is absent, expired or revoked;
  // the server decides, never the client. §47 §81
  useEffect(() => {
    if (isNativeMobile()) return;
    api.me()
      .then((m) => {
        const displayName = m.displayName?.trim() || 'Account';
        setMe({
          userId: m.userId,
          displayName,
          initials: initialsOf(displayName),
          roles: m.roles.map((role) => role.role),
        });
        setMePermissions(m.permissions ?? []);
      })
      .catch(() => setMe(null));
  }, []);

  const presence = usePresence(Boolean(me), screen);

  useEffect(() => {
    if (screen !== 'home' || !me) return;
    api.home().then(setHome).catch(handleError);
  }, [screen, me]);

  const loadPeople = useCallback(
    async (query: string, filter: PeopleFilter, offset = 0) => {
      const requestId = ++peopleRequest.current;
      if (offset === 0) setPeopleLoading(true);
      else setPeopleLoadingMore(true);
      try {
        const res = await api.people({ q: query, filter, limit: 50, offset });
        if (requestId !== peopleRequest.current) return;
        setPeople((current) => offset === 0 ? res.items : [...current, ...res.items]);
        setPeopleTotal(res.total);
      } catch (e) {
        if (requestId !== peopleRequest.current) return;
        handleError(e);
      } finally {
        if (requestId === peopleRequest.current) {
          setPeopleLoading(false);
          setPeopleLoadingMore(false);
        }
      }
    },
    [],
  );

  const searchPeople = useCallback((query: string, filter: PeopleFilter) => {
    void loadPeople(query, filter);
  }, [loadPeople]);

  const loadMorePeople = useCallback((query: string, filter: PeopleFilter) => {
    void loadPeople(query, filter, people.length);
  }, [loadPeople, people.length]);

  const searchPeopleFromHeader = useCallback((query: string) => {
    void loadPeople(query, peopleFilter);
  }, [loadPeople, peopleFilter]);

  const loadMoreVisits = useCallback(() => {
    if (!visitsData || visitsLoadingMore || !visitsData.hasMore) return;
    setVisitsLoadingMore(true);
    setVisitsError(null);
    api.visits(visitTab, { limit: 50, offset: visitsData.items.length })
      .then((next) => setVisitsData((current) => current ? {
        ...next,
        items: [...current.items, ...next.items],
      } : next))
      .catch((e) => setVisitsError(e instanceof ApiError && e.status === 403
        ? { message: 'Your account does not have permission to view these visits.', recoverable: false }
        : { message: 'More visits could not be loaded. You can retry without losing this list.', recoverable: true }))
      .finally(() => setVisitsLoadingMore(false));
  }, [visitTab, visitsData, visitsLoadingMore]);

  useEffect(() => {
    if (screen !== 'visits' || !me) return;
    const requestId = ++visitsRequest.current;
    setVisitsLoading(true);
    setVisitsError(null);
    api.visits(visitTab, { limit: 50 })
      .then((data) => {
        if (requestId === visitsRequest.current) setVisitsData(data);
      })
      .catch((e) => {
        if (requestId !== visitsRequest.current) return;
        if (e instanceof ApiError && e.status === 401) {
          setMe(null);
          return;
        }
        setVisitsError(e instanceof ApiError && e.status === 403
          ? { message: 'Your account does not have permission to view these visits.', recoverable: false }
          : { message: 'The visit list did not respond. Your selected category is unchanged.', recoverable: true });
      })
      .finally(() => {
        if (requestId === visitsRequest.current) setVisitsLoading(false);
      });
  }, [screen, me, visitTab, visitsRetry]);

  useEffect(() => {
    if (screen !== 'visits' || visitsLoading) return;
    const frame = window.requestAnimationFrame(() => window.scrollTo(0, visitsScroll.current));
    return () => window.cancelAnimationFrame(frame);
  }, [screen, visitsLoading]);

  useEffect(() => {
    if (screen !== 'field-mode' || !me) return;
    api.fieldStop(fieldPlanId ?? undefined).then(setFieldStop).catch(handleError);
  }, [screen, me, fieldPlanId]);

  useEffect(() => {
    if (screen !== 'trip-wizard' || !me) return;
    api.tripCandidates()
      .then((c) => {
        setWizardVisits(c.visits);
        setWizardTeams(c.teams);
        setUnresolvedCount(c.unresolvedCount);
        setTripDate(c.defaultDate);
      })
      .catch(handleError);
  }, [screen, me]);

  useEffect(() => {
    if (screen !== 'person' || !me || !selectedPersonId) return;
    setPerson(null);
    setSelectedCaseId('');
    api.person(selectedPersonId).then(setPerson).catch(handleError);
    api.map().then((m) => setTeamOptions(m.teams.map(({ id, name }) => ({ id, name }))))
      .catch(handleError);
  }, [screen, me, selectedPersonId]);

  useEffect(() => {
    if (screen !== 'map' || !me) return;
    let active = true;
    const load = () => void api.map().then((m) => {
      if (!active) return;
      setMapData(m);
      setMapTeam((current) => {
        if (current === 'all' && m.teams[0]) return m.teams[0].id;
        if (current !== 'all' && !m.teams.some((team) => team.id === current)) {
          return m.teams[0]?.id ?? 'all';
        }
        return current;
      });
    }).catch(handleError);
    load();
    const timer = window.setInterval(load, 30_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [screen, me]);

  useEffect(() => {
    if ((screen !== 'team-route' && screen !== 'teams') || !me) return;
    api.map().then(async (m) => {
      const teamId = teamRoute?.teamId ?? m.teams[0]?.id;
      if (teamId) setTeamRoute(await api.teamRoute(teamId));
    }).catch(handleError);
  }, [screen, me]);

  useEffect(() => {
    if (screen !== 'locations-review' || !me) return;
    api.unresolvedLocations().then((r) => setUnresolved(r.items)).catch(handleError);
  }, [screen, me]);

  useEffect(() => {
    if (screen !== 'calendar' || !me) return;
    const requestId = ++calendarRequest.current;
    setCalendarLoading(true);
    setCalendarError(null);
    api.calendar(calendarView, calendarDate)
      .then((data) => {
        if (requestId === calendarRequest.current) setCalendar(data);
      })
      .catch((e) => {
        if (requestId !== calendarRequest.current) return;
        if (e instanceof ApiError && e.status === 401) {
          setMe(null);
          return;
        }
        setCalendarError(e instanceof ApiError && e.status === 403
          ? { message: 'Your account does not have permission to view this calendar.', recoverable: false }
          : { message: 'The calendar service did not respond. Your date and view are unchanged.', recoverable: true });
      })
      .finally(() => {
        if (requestId === calendarRequest.current) setCalendarLoading(false);
      });
  }, [screen, me, calendarView, calendarDate, calendarRetry]);

  useEffect(() => {
    if (screen !== 'calendar' || calendarLoading) return;
    const frame = window.requestAnimationFrame(() => window.scrollTo(0, calendarScroll.current));
    return () => window.cancelAnimationFrame(frame);
  }, [screen, calendarLoading]);

  useEffect(() => {
    if (screen !== 'profile' || !me) return;
    api.profile().then(setProfile).catch(handleError);
  }, [screen, me]);

  useEffect(() => {
    if (screen !== 'help' || !me) return;
    api.help().then((r) => setHelpTopics(r.topics)).catch(handleError);
  }, [screen, me]);

  const loadCallCenter = useCallback(async () => {
    const data = await api.callCenter();
    setCallCenter(data);
    setError(null);
  }, []);

  useEffect(() => {
    if (screen !== 'call-center' || !me) return;
    let refreshPending = false;
    let active = true;
    const refresh = async () => {
      if (refreshPending || !active) return;
      refreshPending = true;
      try {
        await loadCallCenter();
      } catch (cause) {
        if (active) handleError(cause);
      } finally {
        refreshPending = false;
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };

    setCallCenter(null);
    void refresh();
    const timer = window.setInterval(refreshWhenVisible, 30_000);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [screen, me, loadCallCenter]);

  useEffect(() => {
    if (screen === 'call-center') return;
    setCallCenterContact(null);
    setCallCenterConnectRequest(0);
  }, [screen]);

  useEffect(() => {
    if (screen !== 'admin' || !me) return;
    setAdminData(null);
    setAdminAccessMessage(null);
    setAdminAccessReason(null);
    setAdminAccessError(null);
    api.admin(adminTab).then((data) => {
      setAdminData(data);
      setAdminAccessMessage(null);
      setAdminAccessReason(null);
    }).catch((e) => {
      if (e instanceof ApiError && e.status === 403) {
        setAdminAccessReason(e.reason ?? 'PERMISSION_NOT_GRANTED');
        setAdminAccessMessage(adminAccessCopy(e.reason));
        return;
      }
      handleError(e);
    });
  }, [screen, me, adminTab]);

  useEffect(() => {
    if (screen !== 'admin' || adminTab !== 'call-operations' || !me) return;
    const timer = window.setInterval(() => {
      void api.admin('call-operations').then((data) => {
        setAdminData(data);
        setAdminAccessMessage(null);
        setAdminAccessReason(null);
      }).catch((error) => {
        if (error instanceof ApiError && error.status === 403) {
          setAdminData(null);
          setAdminAccessReason(error.reason ?? 'PERMISSION_NOT_GRANTED');
          setAdminAccessMessage(adminAccessCopy(error.reason));
        }
      });
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [screen, adminTab, me]);

  /**
   * Planning is a single server call. The step-4 animation is cosmetic; the
   * wizard does not advance until the plan comes back. §16
   */
  const runPlan = useCallback(async () => {
    setPlanning(true);
    setReview(null);
    setError(null);
    try {
      setReview(await api.planTrip({
        visitIds: wizardVisits.filter((v) => v.selected).map((v) => v.id),
        teamIds: wizardTeams.filter((t) => t.selected).map((t) => t.id),
        date: tripDate,
        startTime,
      }));
      setWizardStep(5);
    } catch (e) {
      handleError(e);
      setWizardStep(3);
    } finally {
      setPlanning(false);
    }
  }, [wizardVisits, wizardTeams, tripDate, startTime]);

  const submitOutcome = useCallback(async () => {
    if (!fieldStop || !outcome) return;
    await withAutoSave(async () => {
      const next = await api.completeStop({
        stopId: fieldStop.stopId,
        routePlanId: fieldStop.routePlanId,
        outcome,
        note: outcomeNote,
        recordIds: fieldStop.records.filter((r) => r.selected).map((r) => r.id),
      });
      setOutcomeOpen(false);
      setOutcome(null);
      setOutcomeNote('');
      // The server decides what comes next; null means the route is finished.
      setFieldStop(next.nextStop);
      setToast(next.nextStop ? 'Stop completed' : 'Route complete');
    });
  }, [fieldStop, outcome, outcomeNote]);

  function handleError(e: unknown) {
    if (e instanceof ApiError && e.status === 401) {
      setMe(null);
      return;
    }
    // Show the server's reason code, never a raw stack. §90
    setError(e instanceof ApiError ? e.reason ?? 'Something went wrong.' : 'Something went wrong.');
  }

  function requiresAdminStepUp(error: unknown): boolean {
    return error instanceof ApiError && error.status === 403 &&
      (error.reason === 'STEP_UP_REQUIRED' || error.reason === 'WEBAUTHN_REQUIRED');
  }

  function securityKeyError(error: unknown): string {
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      return 'Security-key verification was canceled or unavailable. On iPhone, open Veritas in Safari and retry with NFC, USB-C, or Lightning.';
    }
    if (error instanceof ApiError && error.reason === 'CREDENTIAL_UNKNOWN') {
      return 'This security key is not registered to your account.';
    }
    return 'Security-key verification failed. Try again without leaving this screen.';
  }

  async function verifyAdminAccess(): Promise<void> {
    setAdminAccessBusy(true);
    setAdminAccessError(null);
    try {
      const options = await api.beginWebAuthnStepUp();
      await api.completeWebAuthnStepUp(options);
      const data = await api.admin(adminTab);
      setAdminData(data);
      setAdminAccessMessage(null);
      setAdminAccessReason(null);
      setToast('Live Operations unlocked for five minutes.');
    } catch (error) {
      setAdminAccessError(securityKeyError(error));
    } finally {
      setAdminAccessBusy(false);
    }
  }

  async function withFreshAdminAccess<T>(operation: () => Promise<T>): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (!requiresAdminStepUp(error)) throw error;
      const options = await api.beginWebAuthnStepUp();
      await api.completeWebAuthnStepUp(options);
      setToast('Security key verified for five minutes.');
      return operation();
    }
  }

  async function applyAdminChange(change: {
    userId: string; kind: 'role' | 'scope'; value: string;
  }): Promise<void> {
    if (change.kind === 'role') await api.changeUserRole(change.userId, change.value);
    else await api.changeUserScope(change.userId, change.value);
    const next = await api.admin('users');
    setAdminData(next);
    setAdminUser(next.users.find((user) => user.id === change.userId) ?? null);
    setToast(change.kind === 'role' ? 'Role updated' : 'Data scope updated');
  }

  async function requestAdminChange(change: {
    userId: string; kind: 'role' | 'scope'; value: string;
  }): Promise<void> {
    setSaveState('saving');
    setError(null);
    try {
      await applyAdminChange(change);
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2600);
    } catch (error) {
      setSaveState('idle');
      if (!requiresAdminStepUp(error)) {
        handleError(error);
        return;
      }
      try {
        const options = await api.beginWebAuthnStepUp();
        setPendingAdminChange(change);
        setAdminStepUpOptions(options);
        setAdminStepUpError(null);
        setAdminStepUpOpen(true);
      } catch (challengeError) {
        handleError(challengeError);
      }
    }
  }

  async function completeAdminStepUp(): Promise<void> {
    if (!pendingAdminChange || !adminStepUpOptions) return;
    setAdminStepUpBusy(true);
    setAdminStepUpError(null);
    try {
      await api.completeWebAuthnStepUp(adminStepUpOptions);
      await applyAdminChange(pendingAdminChange);
      setAdminStepUpOpen(false);
      setPendingAdminChange(null);
      setAdminStepUpOptions(null);
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2600);
    } catch (error) {
      setSaveState('idle');
      setAdminStepUpError(securityKeyError(error));
      try {
        setAdminStepUpOptions(await api.beginWebAuthnStepUp());
      } catch {
        setAdminStepUpOptions(null);
      }
    } finally {
      setAdminStepUpBusy(false);
    }
  }

  async function submitCreateUser() {
    const allowedRoles = adminData?.roles.map((role) => role.name) ?? [];
    const validation = validateCreateUser(createUser, allowedRoles);
    if (Object.keys(validation).length > 0) {
      setCreateUserErrors(validation);
      return;
    }

    setCreateUserSubmitting(true);
    setCreateUserErrors({});
    try {
      await api.createUser({
        displayName: createUser.displayName.trim(),
        email: createUser.email.trim().toLowerCase(),
        role: createUser.role,
      });
      setAdminData(await api.admin('users'));
      setCreateUserOpen(false);
      setCreateUser({ displayName: '', email: '', role: 'Administrator' });
      setToast('Account created and invitation sent.');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setMe(null);
        return;
      }
      const body = e instanceof ApiError && typeof e.body === 'object' && e.body
        ? e.body as {
            error?: string;
            accountId?: string;
            fieldErrors?: CreateUserErrors;
            message?: string;
          }
        : null;
      if (body?.fieldErrors) {
        setCreateUserErrors(body.fieldErrors);
      } else if (e instanceof ApiError && e.reason === 'user_exists') {
        setCreateUserErrors({ email: 'An account with this email address already exists.' });
      } else if (e instanceof ApiError && e.status === 403) {
        setCreateUserErrors({ form: 'Your account is not permitted to create the selected role.' });
      } else if (body?.error === 'mail_not_configured') {
        setCreateUserErrors({
          form: 'Invitation email is not configured. No account was created.',
        });
      } else if (body?.error === 'invitation_delivery_failed' && body.accountId) {
        setAdminData(await api.admin('users'));
        setCreateUserOpen(false);
        setCreateUser({ displayName: '', email: '', role: 'Administrator' });
        setToast('Account created, but delivery failed. Open the account and resend the invitation.');
      } else {
        setCreateUserErrors({ form: 'The account could not be created. Check the fields and try again.' });
      }
    } finally {
      setCreateUserSubmitting(false);
    }
  }

  /**
   * Auto-save affordance. §10 keeps the ~900ms "Saving..." then "Saved"
   * rhythm, but the mutation is already durable before this runs — the delay is
   * cosmetic and never gates the write.
   */
  /** Permissions held by the signed-in user, for courtesy hiding only. §35 */
  const heldPermissions = new Set<string>(mePermissions);
  useEffect(() => {
    if (screen !== 'av-review' || !mePermissions.includes('av.review')) return;
    void api.avTasks().then((result) => setAvTasks(result.items)).catch(handleError);
  }, [screen, mePermissions]);

  const runImport = useCallback(async (load: () => Promise<ImportPreview>) => {
    setImporting(true);
    try { setImportPreview(await load()); }
    catch (e) { handleError(e); }
    finally { setImporting(false); }
  }, []);

  function renderAuth() {
    if (isNativeMobile()) {
      return (
        <MobileActivationScreen
          busy={mobileActivationBusy}
          error={mobileActivationError}
          onVerify={(username, password, code) => {
            setMobileActivationBusy(true);
            setMobileActivationError(null);
            void import('./mobile/secureChannel.js')
              .then((mobile) => mobile.activateMobileSession(username, password, code))
              .then((session) => {
                const displayName = session.displayName?.trim() || 'Account';
                setMe({
                  userId: session.userId,
                  displayName,
                  initials: initialsOf(displayName),
                  roles: session.roles.map((role) => role.role),
                });
                setMePermissions(session.permissions);
              })
              .catch((cause) => setMobileActivationError(mobileActivationCopy(cause)))
              .finally(() => setMobileActivationBusy(false));
          }}
        />
      );
    }
    const policy = passwordPolicy ?? { minLength: 15, maxLength: 128, summary: [] };
    const steps: FirstSignInStep[] =
      ['password', 'totp', 'security_key', 'recovery_codes'];

    if (authStage === 'forgot') {
      return (
        <ForgotPasswordScreen
          sent={forgotSent}
          onSubmit={(email) => void api.requestPasswordReset(email)
            .then(() => setForgotSent(true))}
          onBack={() => { setForgotSent(false); setAuthStage('sign_in'); }}
        />
      );
    }
    if (authStage === 'reset') {
      return (
        <PasswordResetScreen
          policy={policy} error={authError}
          onSubmit={(pw) => {
            if (!setupToken) {
              setAuthError('This setup link is invalid or has expired.');
              return;
            }
            void api.acceptPasswordToken(setupToken, pw)
              .then(async (result) => {
                setAuthError(null);
                if (!result.firstSignInRequired) {
                  const available: MfaMethod[] = [];
                  if (result.totpEnrolled) available.push('totp');
                  if (result.webauthnEnrolled) available.push('webauthn');
                  setMfaAvailable(available);
                  setMfaMethod(result.totpEnrolled ? 'totp' : 'webauthn');
                  setAuthStage('mfa');
                  return;
                }
                if (!result.totpEnrolled) {
                  const setup = await api.setupTotp();
                  setTotpSetup(setup);
                  setFirstStep('totp');
                } else if (!result.webauthnEnrolled && !result.recoveryCodesGenerated) {
                  setFirstStep('security_key');
                } else if (!result.recoveryCodesGenerated) {
                  const generated = await api.regenerateRecoveryCodes();
                  setRecoveryCodes(generated.codes);
                  setFirstStep('recovery_codes');
                } else {
                  window.location.reload();
                }
                setAuthStage('first_sign_in');
              })
              .catch((e) => setAuthError(e.reason ?? String(e)));
          }}
        />
      );
    }
    if (authStage === 'mfa') {
      return (
        <MfaPromptScreen
          available={mfaAvailable} method={mfaMethod} error={authError}
          onMethodChange={setMfaMethod}
          onSubmit={(code) => void api.verifyMfa(mfaMethod, code)
            .then(() => window.location.reload())
            .catch((e) => setAuthError(String(e)))}
          onUseSecurityKey={() => void api.verifyWebAuthn()
            .then(() => window.location.reload())
            .catch((e) => setAuthError(e.reason ?? String(e)))}
        />
      );
    }
    if (authStage === 'first_sign_in') {
      if (firstStep === 'password') {
        return (
          <FirstSignInPasswordScreen
            policy={policy} error={authError} steps={steps}
            onSubmit={(pw) => void api.setInitialPassword(pw)
              .then(() => api.setupTotp())
              .then((setup) => {
                setAuthError(null);
                setTotpSetup(setup);
                setFirstStep('totp');
              })
              .catch((e) => setAuthError(e.reason ?? String(e)))}
          />
        );
      }
      if (firstStep === 'totp') {
        return (
          <FirstSignInTotpScreen
            secret={totpSetup?.secret ?? ''} otpauthUri={totpSetup?.uri ?? ''}
            error={authError} steps={steps}
            onVerify={(code) => void api.verifyMfa('totp', code)
              .then(() => {
                setAuthError(null);
                setFirstStep('security_key');
              })
              .catch((e) => setAuthError(e.reason ?? String(e)))}
          />
        );
      }
      if (firstStep === 'security_key') {
        return (
          <FirstSignInSecurityKeyScreen
            error={authError} steps={steps}
            onEnroll={(name) => void api.enrollSecurityKey(name)
              .then(() => api.regenerateRecoveryCodes())
              .then((r) => {
                setAuthError(null);
                setRecoveryCodes(r.codes);
                setFirstStep('recovery_codes');
              })
              .catch((e) => setAuthError(e.reason ?? String(e)))}
            onSkip={() => void api.deferSecurityKey()
              .then(() => api.regenerateRecoveryCodes())
              .then((r) => {
                setAuthError(null);
                setRecoveryCodes(r.codes);
                setFirstStep('recovery_codes');
              })
              .catch((e) => setAuthError(e.reason ?? String(e)))}
          />
        );
      }
      return (
        <FirstSignInRecoveryCodesScreen
          codes={recoveryCodes} steps={steps}
          onDone={() => window.location.reload()}
        />
      );
    }

    return (
      <SignInScreen
        ssoEnabled={ssoEnabled} localEnabled={localSignInEnabled} error={authError}
        onSso={() => { window.location.href = '/auth/saml/start'; }}
        onLocalSignIn={(u, pw) => void api.signIn(u, pw)
          .then(async (r) => {
            setAuthError(null);

            if (r.firstSignInRequired) {
              setAuthStage('first_sign_in');

              if (r.mustChangePassword) {
                setFirstStep('password');
                return;
              }

              if (!r.totpEnrolled) {
                const setup = await api.setupTotp();
                setTotpSetup(setup);
                setFirstStep('totp');
                return;
              }

              if (!r.webauthnEnrolled && !r.recoveryCodesGenerated) {
                setFirstStep('security_key');
                return;
              }

              if (!r.recoveryCodesGenerated) {
                const generated = await api.regenerateRecoveryCodes();
                setRecoveryCodes(generated.codes);
                setFirstStep('recovery_codes');
                return;
              }
            }

            if (r.mfaRequired) {
              const available: MfaMethod[] = [];
              if (r.totpEnrolled) available.push('totp');
              if (r.webauthnEnrolled) available.push('webauthn');
              setMfaAvailable(available.length ? available : ['totp']);
              setMfaMethod(r.totpEnrolled ? 'totp' : 'webauthn');
              setAuthStage('mfa');
              return;
            }

            window.location.reload();
          })
          .catch((e) => setAuthError(e.reason ?? 'Sign in failed'))}
        onForgot={() => setAuthStage('forgot')}
      />
    );
  }

  async function openCallCenterForPerson(personId: string): Promise<void> {
    try {
      const contact = await api.person(personId);
      if (!contact.phone) {
        setToast('No phone number is available for this person');
        return;
      }
      openCallCenterWithContact({
        id: contact.id, name: contact.name, phone: contact.phone,
      });
    } catch (cause) { handleError(cause); }
  }

  function openCallCenterWithContact(contact: CallCenterContact): void {
    setCallCenterContact(contact);
    setCallCenterConnectRequest((request) => request + 1);
    setScreen('call-center');
  }

  const withAutoSave = useCallback(async (mutate: () => Promise<void>) => {
    setSaveState('saving');
    setError(null);
    try {
      await mutate();
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2600); // §10 toast duration
    } catch (e) {
      setSaveState('idle');
      handleError(e);
    }
  }, []);

  useEffect(() => {
    setError(null);
  }, [screen]);

  useEffect(() => {
    if (isNativeMobile() && screen !== 'call-center') setScreen('call-center');
  }, [screen]);

  if (!me) return renderAuth();
  const canManagePlanning = me.roles.some((role) =>
    role === 'Manager' || role === 'Administrator' || role === 'Super Administrator');

  return (
    <AppShell
      screen={screen}
      canManagePlanning={canManagePlanning}
      onNavigate={(next) => {
        if (!window.dispatchEvent(new Event('veritas:before-navigation', { cancelable: true }))) return;
        if (screen === 'calendar' && next !== 'calendar') calendarScroll.current = window.scrollY;
        if (screen === 'visits' && next !== 'visits') visitsScroll.current = window.scrollY;
        if (next === 'call-center') {
          setCallCenterContact(null);
          setCallCenterConnectRequest(0);
        }
        setScreen(next);
      }}
      saveState={saveState}
      userInitials={me.initials}
      presence={presence}
      onAccountClick={isNativeMobile() ? undefined : () => {
        if (window.dispatchEvent(new Event('veritas:before-navigation', { cancelable: true }))) setScreen('profile');
      }}
      onSearch={(value) => {
        if (window.dispatchEvent(new Event('veritas:before-navigation', { cancelable: true }))) searchPeopleFromHeader(value);
      }}
    >
      {error && (
        <div role="alert" className="mt-4 p-3 bg-alert-100 border border-alert-300 rounded-lg">
          <p className="text-base text-alert-800">{error}</p>
        </div>
      )}

      {screen === 'home' && (
        <HomeScreen
          data={home}
          onNavigate={setScreen}
          onOpenTodaysPlan={() => {
            const teamId = home?.upcoming[0]?.id;
            if (!teamId) return;
            api.teamRoute(teamId).then((route) => {
              setTeamRoute(route);
              setScreen('team-route');
            }).catch(handleError);
          }}
          onViewFollowUps={() => { setPeopleFilter('follow_up'); setScreen('people'); }}
          onOpenRoute={(teamId) => api.teamRoute(teamId).then((route) => {
            setTeamRoute(route);
            setScreen('team-route');
          }).catch(handleError)}
        />
      )}

      {screen === 'field-work' && (
        <FieldWorkScreen
          manager={canManagePlanning}
          canUseForms={heldPermissions.has('records.update')}
          onVisits={() => setScreen('visits')}
          onRoutes={() => setScreen('teams')}
          onMap={() => setScreen('map')}
          onCalendar={() => setScreen('calendar')}
          onForms={() => setScreen('forms')} />
      )}

      {screen === 'people' && (
        <PeopleScreen
          people={people}
          total={peopleTotal}
          filter={peopleFilter}
          loading={peopleLoading}
          loadingMore={peopleLoadingMore}
          onFilterChange={setPeopleFilter}
          onSearch={searchPeople}
          onLoadMore={loadMorePeople}
          onSelect={(id) => { setSelectedPersonId(id); setScreen('person'); }}
          canCall={heldPermissions.has('records.update')}
          onCall={openCallCenterForPerson}
          canImport={heldPermissions.has('records.create')}
          onImport={() => { setImportPreview(null); setScreen('import'); }}
        />
      )}

      {screen === 'visits' && (
        <VisitsScreen
          data={visitsData}
          tab={visitTab}
          loading={visitsLoading}
          loadingMore={visitsLoadingMore}
          error={visitsError}
          canCreateTrip={false}
          canCall={heldPermissions.has('records.update')}
          canSchedule={heldPermissions.has('records.update')}
          onRetry={() => setVisitsRetry((attempt) => attempt + 1)}
          onLoadMore={loadMoreVisits}
          onTabChange={(nextTab) => {
            visitsScroll.current = 0;
            setVisitsData(null);
            setVisitTab(nextTab);
          }}
          onNewTrip={() => {
            visitsScroll.current = window.scrollY;
            setWizardStep(1); setReview(null); setScreen('trip-wizard');
          }}
          onSelectStop={(personId) => {
            visitsScroll.current = window.scrollY;
            visitsOpenedPerson.current = true;
            calendarOpenedPerson.current = false;
            setSelectedPersonId(personId);
            setScreen('person');
          }}
          onCall={openCallCenterForPerson}
          onSchedule={async (personId) => {
            const selected = await api.person(personId);
            if (selected.doNotVisit) { setToast('DO_NO_VISIT records cannot be scheduled'); return; }
            setPerson(selected);
            setSelectedPersonId(personId);
            setSchedule(scheduleVisitDefaults(new Date().toISOString().slice(0, 10)));
            setScheduleOpen(true);
          }}
        />
      )}

      {screen === 'trip-wizard' && (
        <TripWizard
          step={wizardStep}
          visits={wizardVisits}
          teams={wizardTeams}
          unresolvedCount={unresolvedCount}
          date={tripDate}
          startTime={startTime}
          review={review}
          planning={planning}
          onStepChange={setWizardStep}
          onToggleVisit={(id) => setWizardVisits((vs) =>
            vs.map((v) => (v.id === id ? { ...v, selected: !v.selected } : v)))}
          onToggleTeam={(id) => setWizardTeams((ts) =>
            ts.map((t) => (t.id === id ? { ...t, selected: !t.selected } : t)))}
          onDateChange={setTripDate}
          onStartTimeChange={setStartTime}
          onPlan={() => void runPlan()}
          onOpenRoutes={() => {
            const teamId = review?.teams[0]?.teamId;
            if (!teamId) return;
            api.teamRoute(teamId).then((route) => {
              setTeamRoute(route);
              setScreen('team-route');
            }).catch(handleError);
          }}
          onViewMap={() => {
            if (review?.teams[0]) setMapTeam(review.teams[0].teamId);
            setScreen('map');
          }}
          onViewSchedule={() => {
            setCalendarDate(tripDate);
            setCalendarView('Day');
            setScreen('calendar');
          }}
          onAdvancedAnalysis={() => setAnalysisOpen(true)}
          onCancel={() => setScreen(canManagePlanning ? 'team-assignments' : 'visits')}
        />
      )}

      {screen === 'field-mode' && (
        <FieldModeScreen
          stop={fieldStop}
          onCall={(id) => {
            const record = fieldStop?.records.find((item) => item.id === id);
            if (record?.phone) openCallCenterWithContact({
              id: record.id, name: record.name, phone: record.phone,
            });
          }}
          onToggleRecord={(id) => setFieldStop((s) => s && ({
            ...s,
            records: s.records.map((r) =>
              r.id === id ? { ...r, selected: !r.selected } : r),
          }))}
          onArrived={() => setOutcomeOpen(true)}
          onSkip={() => void withAutoSave(async () => {
            setFieldStop((await api.skipStop(fieldStop!.stopId, fieldStop!.routePlanId)).nextStop);
            setToast('Stop skipped');
          })}
          onExit={() => setScreen('visits')}
        />
      )}

      {screen === 'person' && (
        <PersonScreen
          person={person}
          backLabel={calendarOpenedPerson.current ? 'Calendar' : visitsOpenedPerson.current ? 'Visits' : 'People'}
          onCall={() => {
            if (!person?.phone) return;
            openCallCenterWithContact({ id: person.id, name: person.name, phone: person.phone });
          }}
          onLogCall={() => setCallLogOpen(true)}
          onSchedule={() => setScheduleOpen(true)}
          onNote={() => { setNoteText(''); setNoteOpen(true); }}
          onSubmitAddress={() => { setAvAddress(''); setAvNotes(''); setAvAddressOpen(true); }}
          onReassign={heldPermissions.has('routes.manage')
            ? () => { setReassignTeamId(''); setReassignOpen(true); } : undefined}
          onBack={() => {
            if (calendarOpenedPerson.current) {
              calendarOpenedPerson.current = false;
              setScreen('calendar');
            } else if (visitsOpenedPerson.current) {
              visitsOpenedPerson.current = false;
              setScreen('visits');
            } else setScreen('people');
          }}
        />
      )}

      {screen === 'team-route' && (
        <TeamRouteScreen
          data={teamRoute}
          canReorder={Boolean(teamRoute?.canReorder)}
          onTeamChange={(id) => api.teamRoute(id, teamRoute?.tripId).then(setTeamRoute).catch(handleError)}
          onTripChange={(tripId, firstTeamId) =>
            api.teamRoute(firstTeamId, tripId).then(setTeamRoute).catch(handleError)}
          onReorder={async (routePlanId, stopIds) => {
            const updated = await api.reorderTeamRoute(routePlanId, stopIds);
            setTeamRoute(updated);
            return updated;
          }}
          onMoveStop={heldPermissions.has('routes.manage') ? async (routePlanId, stopId, targetRoutePlanId) => {
            const result = await api.moveRouteStop(routePlanId, stopId, targetRoutePlanId);
            if (result.error) throw new Error(result.error);
            const updated = await api.teamRoute(teamRoute!.teamId, teamRoute?.tripId);
            setTeamRoute(updated);
            return updated;
          } : undefined}
          onMoveStops={heldPermissions.has('routes.manage') ? async (routePlanId, stopIds, targetRoutePlanId) => {
            const result = await api.moveRouteStops(routePlanId, stopIds, targetRoutePlanId);
            if (result.error) throw new Error(result.error);
            const updated = await api.teamRoute(teamRoute!.teamId, teamRoute?.tripId);
            setTeamRoute(updated);
            return updated;
          } : undefined}
          onStartField={() => void api.fieldStop(teamRoute?.routePlanId).then((stop) => {
            setFieldPlanId(teamRoute?.routePlanId ?? null);
            setFieldStop(stop);
            setScreen('field-mode');
          }).catch(handleError)}
          onShare={() => void shareTrip(teamRoute)}
          onAddToCalendar={() => void withAutoSave(async () => {
            const exported = await api.addTripToCalendar(teamRoute!.teamId);
            downloadIcs(exported.ics, teamRoute!.dateLabel);
            setToast('Calendar file downloaded');
          })}
        />
      )}

      {screen === 'teams' && (
        teamRoute?.routePlanId ? <TeamRouteScreen
          data={teamRoute}
          canReorder={Boolean(teamRoute.canReorder)}
          onTeamChange={(id) => api.teamRoute(id, teamRoute.tripId).then(setTeamRoute).catch(handleError)}
          onTripChange={(tripId, firstTeamId) =>
            api.teamRoute(firstTeamId, tripId).then(setTeamRoute).catch(handleError)}
          onReorder={async (routePlanId, stopIds) => {
            const updated = await api.reorderTeamRoute(routePlanId, stopIds);
            setTeamRoute(updated);
            return updated;
          }}
          onStartField={() => void api.fieldStop(teamRoute.routePlanId).then((stop) => {
            setFieldPlanId(teamRoute.routePlanId ?? null);
            setFieldStop(stop);
            setScreen('field-mode');
          }).catch(handleError)}
          onShare={() => void shareTrip(teamRoute)}
          onAddToCalendar={() => void withAutoSave(async () => {
            if (!teamRoute) return;
            const exported = await api.addTripToCalendar(teamRoute.teamId);
            downloadIcs(exported.ics, teamRoute.dateLabel);
            setToast('Calendar file downloaded');
          })}
        /> : <section className="pt-8 space-y-3">
          <h1>{canManagePlanning ? 'No routes planned yet' : 'My Team'}</h1>
          <p className="text-base text-neutral-700">{canManagePlanning
            ? 'Set coverage and review clusters in Planning before creating routes.'
            : 'Your team does not have an assigned trip yet. Your manager can plan one.'}</p>
          {canManagePlanning && <button className="btn btn-primary"
            onClick={() => setScreen('team-assignments')}>Open Planning</button>}
        </section>
      )}

      {screen === 'map' && (
        <MapScreen
          data={mapData}
          selectedTeamId={mapTeam}
          canReorder={heldPermissions.has('routes.manage')}
          onTeamFilter={setMapTeam}
          onSelectStop={() => { /* MapScreen owns visual stop selection. */ }}
          onReorder={async (routePlanId, stopIds) => {
            await api.reorderTeamRoute(routePlanId, stopIds);
            const updated = await api.map();
            setMapData(updated);
            return updated;
          }}
          onOpenRoute={(teamId) => api.teamRoute(teamId).then((route) => {
            setTeamRoute(route);
            setScreen('team-route');
          }).catch(handleError)}
        />
      )}

      {screen === 'import' && (
        <ImportScreen
          preview={importPreview}
          uploading={importing}
          error={error}
          onUpload={(f) => void runImport(() => api.importFile(f))}
          onPaste={(t) => void runImport(() => api.importPaste(t))}
          onMappingChange={(h, field) => setImportPreview((p) => p && ({
            ...p,
            mapping: p.mapping.map((m) =>
              m.sourceHeading === h ? { ...m, field, confident: true } : m),
          }))}
          onContinue={() => void withAutoSave(async () => {
            const result = await api.importCommit(importPreview!.jobId, importPreview!.mapping);
            setImportPreview(null);
            setToast(`Imported ${result.personsCreated} ${result.personsCreated === 1 ? 'person' : 'people'} and ${result.visitsCreated} ${result.visitsCreated === 1 ? 'visit' : 'visits'}`);
            setScreen('locations-review');
          })}
        />
      )}

      {screen === 'locations-review' && (
        <LocationsReviewScreen
          locations={unresolved}
          onEditAddress={(id) => {
            const location = unresolved.find((item) => item.id === id);
            if (location) setLocationEditor({ id, mode: 'edit', address: location.originalAddress,
              city: location.city ?? '', state: location.state ?? '', zip: location.zip ?? '', lat: '', lng: '' });
          }}
          onPlaceOnMap={(id) => {
            const location = unresolved.find((item) => item.id === id);
            if (location) setLocationEditor({ id, mode: 'place', address: location.originalAddress,
              city: location.city ?? '', state: location.state ?? '', zip: location.zip ?? '', lat: '', lng: '' });
          }}
          onSkip={(id) => void withAutoSave(async () => {
            await api.skipLocation(id);
            setUnresolved((items) => items.filter((item) => item.id !== id));
            setToast('Location deferred');
          })}
        />
      )}

      {screen === 'calendar' && (
        <CalendarScreen
          view={calendarView}
          selectedDate={calendarDate}
          periodLabel={calendar?.periodLabel ?? ''}
          events={calendar?.events ?? []}
          connections={calendar?.connections ?? []}
          disclosureStatement={calendar?.disclosureStatement}
          timeZone={calendar?.timeZone ?? 'America/Phoenix'}
          loading={calendarLoading}
          error={calendarError}
          canManageConnections={mePermissions.includes('integrations.manage')}
          onViewChange={setCalendarView}
          onDateSelect={(date, view) => { setCalendarDate(date); setCalendarView(view); }}
          onPrev={() => setCalendarDate((d) => shiftCalendarDate(d, calendarView, -1))}
          onToday={() => setCalendarDate(dateKeyInTimeZone(new Date(), calendar?.timeZone ?? 'America/Phoenix'))}
          onNext={() => setCalendarDate((d) => shiftCalendarDate(d, calendarView, 1))}
          onRetry={() => setCalendarRetry((value) => value + 1)}
          onManageConnections={() => { setAdminTab('integrations'); setScreen('admin'); }}
          onSelect={(event) => {
            calendarScroll.current = window.scrollY;
            if (event.kind === 'team_trip' && event.teamId) {
              api.teamRoute(event.teamId).then((r) => {
                setTeamRoute(r);
                setScreen('team-route');
              }).catch(handleError);
            } else if (event.personId) {
              calendarOpenedPerson.current = true;
              visitsOpenedPerson.current = false;
              setSelectedPersonId(event.personId);
              setScreen('person');
            }
          }}
        />
      )}

      {screen === 'more' && (
        <MoreScreen
          links={MORE_LINKS}
          heldPermissions={heldPermissions}
          onSelect={(id) => {
            if (id === 'sign-out') {
              void api.logout().finally(() => setMe(null));
              return;
            }
            if (id === 'notifications') {
              if (heldPermissions.has('av.review')) setScreen('av-review');
              else setToast('You have no notifications requiring action.');
              return;
            }
            if (id === 'call-operations') {
              setAdminTab('call-operations'); setScreen('admin'); return;
            }
            if (id === 'users-access') {
              setAdminTab('users'); setScreen('admin'); return;
            }
            if (id === 'audit-history') {
              setAdminTab('audit'); setScreen('admin'); return;
            }
            if (id === 'teams' || id === 'route-settings') {
              setScreen('team-assignments'); return;
            }
            if (id === 'report-problem') {
              setScreen('help'); return;
            }
            setScreen(id as ScreenId);
          }}
        />
      )}

      {screen === 'team-assignments' && canManagePlanning && (
        <TeamAssignmentsScreen
          onOpenRoutes={() => setScreen('teams')}
          onOpenMap={() => setScreen('map')}
          onReviewAddresses={() => setScreen('locations-review')}
          onManualTrip={() => {
            setWizardStep(1); setReview(null); setScreen('trip-wizard');
          }} />
      )}

      {screen === 'av-review' && heldPermissions.has('av.review') && (
        <section className="pt-4">
          <h1>AV address review</h1>
          <p className="text-base text-neutral-700 mb-4">Field-submitted locations awaiting analysis.</p>
          <button className="btn btn-secondary mb-4" onClick={() => void api.avTasks()
            .then((result) => setAvTasks(result.items)).catch(handleError)}>Refresh</button>
          {avTasks.length === 0 && <p>No pending submissions are loaded.</p>}
          {avTasks.map((task) => <article key={task.id} className="border-b border-neutral-300 py-4">
            <h2>{task.caseNumber}</h2>
            <p>Proposed: {task.proposedAddress}</p>
            <p>Prior: {task.priorAddress ?? 'Not recorded'}</p>
            <p>Submitted by {task.submittedBy} on {new Date(task.submittedAt).toLocaleString()}</p>
            {task.notes && <p>Notes: {task.notes}</p>}
            <p>Status: {task.status}</p>
            <button className="btn btn-secondary mt-2" onClick={() => void withAutoSave(async () => {
              const result = await api.startAvTask(task.id);
              if (result.error) throw new Error(result.error);
              setAvSelectedTaskId(task.id);
              setAvValidatedAddress(task.proposedAddress);
              setAvDecisionNotes('');
              setAvDecision('research_required');
              setAvTasks((await api.avTasks()).items);
            })}>Review</button>
          </article>)}
          {avSelectedTaskId && <form className="card mt-5 space-y-3" onSubmit={(event) => {
            event.preventDefault();
            void withAutoSave(async () => {
              const result = await api.decideAvTask(avSelectedTaskId, {
                decision: avDecision, notes: avDecisionNotes,
                ...(avDecision === 'approved' ? {
                  validatedAddress: avValidatedAddress, city: avCity,
                  state: avState.toUpperCase(), zip: avZip,
                  lat: Number(avLat), lng: Number(avLng),
                } : {}),
              });
              if (result.error) throw new Error(result.error);
              setAvSelectedTaskId(null);
              setAvTasks((await api.avTasks()).items);
              setAvUnreadCount((await api.avNotificationCount()).unread);
              setToast(`AV decision saved. Case status: ${result.caseStatus ?? 'updated'}.`);
            });
          }}>
            <h2>Record AV decision</h2>
            <label className="field"><span>Decision</span><select className="input" value={avDecision}
              onChange={(event) => setAvDecision(event.target.value)}>
              <option value="approved">Approved for field deployment</option>
              <option value="research_required">Additional research required</option>
              <option value="unsupported">Address not supported</option>
              <option value="duplicate">Duplicate or previously investigated</option>
              <option value="supervisory_review">Supervisory review required</option>
            </select></label>
            <label className="field"><span>Analysis and evidence</span><textarea className="input min-h-[100px]"
              value={avDecisionNotes} onChange={(event) => setAvDecisionNotes(event.target.value)}
              minLength={10} maxLength={4000} required /></label>
            {avDecision === 'approved' && <>
              <p>Confirm the validated location. Coordinates are required before assigning a field team.</p>
              <label className="field"><span>Validated address</span><input className="input" required
                value={avValidatedAddress} onChange={(event) => setAvValidatedAddress(event.target.value)} /></label>
              <div className="grid gap-3 desktop:grid-cols-3">
                <label className="field"><span>City</span><input className="input" required
                  value={avCity} onChange={(event) => setAvCity(event.target.value)} /></label>
                <label className="field"><span>State code</span><input className="input" required maxLength={2}
                  value={avState} onChange={(event) => setAvState(event.target.value)} /></label>
                <label className="field"><span>ZIP</span><input className="input"
                  value={avZip} onChange={(event) => setAvZip(event.target.value)} /></label>
                <label className="field"><span>Latitude</span><input className="input" type="number" step="any" required
                  value={avLat} onChange={(event) => setAvLat(event.target.value)} /></label>
                <label className="field"><span>Longitude</span><input className="input" type="number" step="any" required
                  value={avLng} onChange={(event) => setAvLng(event.target.value)} /></label>
              </div>
            </>}
            <div className="flex gap-2">
              <button className="btn btn-primary" disabled={avDecisionNotes.trim().length < 10}>Save decision</button>
              <button className="btn btn-secondary" type="button" onClick={() => setAvSelectedTaskId(null)}>Cancel</button>
            </div>
          </form>}
        </section>
      )}

      {screen === 'profile' && (
        <ProfileScreen
          data={profile}
          onAddSecurityKey={() => void api.enrollSecurityKey('Security key')
            .then(() => api.profile())
            .then((p) => { setProfile(p); setToast('Security key added'); })
            .catch(handleError)}
          onRegenerateCodes={() => void withAutoSave(async () => {
            setRecoveryCodes((await api.regenerateRecoveryCodes()).codes);
            setToast('New recovery codes generated');
          })}
          onRevokeSession={(id) => void withAutoSave(async () => {
            await api.revokeSession(id);
            setToast('Session revoked');
          })}
          onSignOutEverywhere={() => void api.revokeAllSessions().then(() => setMe(null))}
        />
      )}

      {screen === 'help' && (
        <HelpScreen
          topics={helpTopics}
          supportLine="Contact your Veritas administrator for anything not covered here."
        />
      )}

      {screen === 'call-center' && (
        <CallCenterScreen data={callCenter} onRefresh={loadCallCenter}
          advancedMobileAvailable={me.roles.some((role) =>
            role === 'Administrator' || role === 'Super Administrator')}
          initialContact={callCenterContact}
          connectOnLoad={Boolean(callCenterContact) && callCenterConnectRequest > 0}
          connectRequestId={callCenterConnectRequest}
          onEditPlaybook={mePermissions.includes('security.manage') ? () => {
            setAdminTab('call-routing');
            setScreen('admin');
          } : undefined}
          onScheduleVisit={(personId) => void api.person(personId).then((selected) => {
            if (selected.doNotVisit) { setToast('DO_NO_VISIT records cannot be scheduled'); return; }
            setPerson(selected);
            setSelectedPersonId(personId);
            setScheduleOpen(true);
          }).catch(handleError)} />
      )}

      {screen === 'forms' && (
        <div className="pt-4 desktop:pt-6 max-w-[760px]">
          <h1 className="mb-1">Forms</h1>
          <p className="mb-5 text-neutral-700">
            Continue the same protected field record from the web, phone, or tablet.
          </p>
          <div className="border border-neutral-300 bg-white p-4 desktop:p-6">
            <FieldWellnessScreen onManageTemplates={!isNativeMobile() && heldPermissions.has('records.update')
              && me?.roles.some((role) => ['Manager', 'Administrator', 'Super Administrator'].includes(role))
              ? () => { setAdminTab('wellness-notes'); setScreen('admin'); } : undefined} />
          </div>
        </div>
      )}

      {screen === 'admin' && (
        <AdminScreen
          tab={adminTab}
          data={adminData}
          accessMessage={adminAccessMessage}
          accessReason={adminAccessReason}
          accessBusy={adminAccessBusy}
          accessError={adminAccessError}
          onVerifyAccess={() => void verifyAdminAccess()}
          onTabChange={setAdminTab}
          onSelectUser={(id) =>
            setAdminUser(adminData?.users.find((u) => u.id === id) ?? null)}
          onCreateUser={() => {
            setCreateUserErrors({});
            setCreateUserOpen(true);
          }}
          onRevokeSession={(id) => void withAutoSave(async () => {
            await api.revokeSession(id);
            setToast('Session revoked');
          })}
          onRunCallCenterDiagnostics={(target) => api.runCallCenterDiagnostics(target)}
          onRefreshAudit={async () => setAdminData(await api.admin('audit'))}
          onOpenCallCenter={() => setScreen('call-center')}
          onConnectStarlink={async (body) => {
            await withFreshAdminAccess(() => api.connectStarlink(body));
            setAdminData(await api.admin('integrations'));
            setToast('Starlink connected. Assign each terminal to a team.');
          }}
          onDisconnectStarlink={async () => {
            await withFreshAdminAccess(() => api.disconnectStarlink());
            setAdminData(await api.admin('integrations'));
            setMapData(null);
            setToast('Starlink disconnected. Cached terminal areas were removed.');
          }}
          onSyncStarlink={async () => {
            await withFreshAdminAccess(() => api.syncStarlink());
            setAdminData(await api.admin('integrations'));
            setToast('Starlink locations refreshed.');
          }}
          onCreateTeam={async (name) => {
            const { team } = await withFreshAdminAccess(() => api.createTeam(name));
            setAdminData(await api.admin('integrations'));
            setTeamOptions((current) => current.some((item) => item.id === team.id)
              ? current : [...current, team].sort((a, b) => a.name.localeCompare(b.name)));
            setMapData(null);
            setToast(`${team.name} created.`);
            return team;
          }}
          onAssignGeography={async (body) => {
            const result = await withFreshAdminAccess(() => api.assignGeography(body));
            setAdminData(await api.admin('integrations'));
            setMapData(null);
            setToast(`${result.updated} records updated.`);
            return result;
          }}
          onSaveStarlinkAssignments={async (assignments) => {
            await withFreshAdminAccess(() => api.saveStarlinkAssignments(assignments));
            setAdminData(await api.admin('integrations'));
            setMapData(null);
            setToast('Starlink terminals assigned to teams.');
          }}
          onAddFieldWellnessCaseOfficer={async (body) => {
            await withFreshAdminAccess(() => api.addFieldWellnessCaseOfficer(body));
            setAdminData(await api.admin('field-wellness'));
            setToast('Case officer encrypted and added.');
          }}
          onSelectFieldWellnessCaseOfficer={async (officerId) => {
            await withFreshAdminAccess(() => api.selectFieldWellnessCaseOfficer(officerId));
            setAdminData(await api.admin('field-wellness'));
            setToast('Case officer assignment updated for all field personnel.');
          }}
          onSaveCallRoster={async (queueId, userIds) => {
            setSaveState('saving');
            try {
              await api.updateCallCenterRoster(queueId, userIds);
              setAdminData(await api.admin('call-routing'));
              setSaveState('saved');
              setToast('Call roster updated. Rotation is active.');
              setTimeout(() => setSaveState('idle'), 2600);
            } catch (cause) {
              setSaveState('idle');
              throw cause;
            }
          }}
          onSaveCallPlaybook={async (entries) => {
            setSaveState('saving');
            try {
              await api.updateCallCenterPlaybook(entries);
              setAdminData(await api.admin('call-routing'));
              setCallCenter(await api.callCenter());
              setSaveState('saved');
              setToast('Conversation playbook updated.');
              setTimeout(() => setSaveState('idle'), 2600);
            } catch (cause) {
              setSaveState('idle');
              throw cause;
            }
          }}
          onDecideCommunicationAccess={async (userId, decision, reason) => {
            await withFreshAdminAccess(() =>
              api.decideCommunicationAccess(userId, decision, reason));
            setAdminData(await api.admin('call-routing'));
            setCallCenter(await api.callCenter());
            setToast(decision === 'approve'
              ? 'Managed phone access approved.' : 'Managed phone access denied.');
          }}
          onTransferCall={async (id, body) => {
            await api.managerTransferCall(id, body);
            setAdminData(await api.admin('call-operations'));
            setToast('Transfer sent to the PBX.');
          }}
          onSaveEscalationTeam={async (assignments) => {
            await api.updateCallCenterEscalationTeam(assignments);
            setAdminData(await api.admin('call-operations'));
            setCallCenter(await api.callCenter());
            setToast('Escalation coverage updated.');
          }}
        />
      )}

      <Sheet
        open={createUserOpen}
        title="Create user"
        onClose={() => {
          if (!createUserSubmitting) setCreateUserOpen(false);
        }}
        actions={<>
          <button type="button" className="btn btn-secondary flex-1"
            disabled={createUserSubmitting} onClick={() => setCreateUserOpen(false)}>Cancel</button>
          <button type="submit" form="create-user-form" className="btn btn-primary flex-1"
            disabled={createUserSubmitting}>{createUserSubmitting ? 'Creating…' : 'Create user'}</button>
        </>}
      >
        <p className="text-base text-neutral-700 mb-4">Create a controlled account and assign one of the five built-in roles. MFA enrollment is required at first sign-in.</p>
        <form id="create-user-form" noValidate aria-busy={createUserSubmitting}
          onSubmit={(event) => { event.preventDefault(); void submitCreateUser(); }}>
          {createUserErrors.form && <p role="alert" className="form-error mb-3">{createUserErrors.form}</p>}
          <label className="field" htmlFor="create-user-display-name"><span>Display name <span aria-hidden>*</span></span></label>
          <input id="create-user-display-name" name="displayName" className="input" autoComplete="name"
            data-initial-focus maxLength={120} required aria-invalid={Boolean(createUserErrors.displayName)}
            aria-describedby={createUserErrors.displayName ? 'create-user-display-name-error' : undefined}
            value={createUser.displayName} onChange={(event) => {
              setCreateUser((value) => ({ ...value, displayName: event.target.value }));
              setCreateUserErrors((value) => ({ ...value, displayName: undefined, form: undefined }));
            }} />
          {createUserErrors.displayName && <p id="create-user-display-name-error" className="field-error">{createUserErrors.displayName}</p>}

          <label className="field mt-3" htmlFor="create-user-email"><span>Email <span aria-hidden>*</span></span></label>
          <input id="create-user-email" name="email" className="input" type="email" inputMode="email"
            autoCapitalize="none" autoCorrect="off" autoComplete="email" maxLength={254} required
            aria-invalid={Boolean(createUserErrors.email)}
            aria-describedby={createUserErrors.email ? 'create-user-email-error' : undefined}
            value={createUser.email} onChange={(event) => {
              setCreateUser((value) => ({ ...value, email: event.target.value }));
              setCreateUserErrors((value) => ({ ...value, email: undefined, form: undefined }));
            }} />
          {createUserErrors.email && <p id="create-user-email-error" className="field-error">{createUserErrors.email}</p>}

          <label className="field mt-3" htmlFor="create-user-role"><span>Role <span aria-hidden>*</span></span></label>
          <select id="create-user-role" name="role" className="input" required value={createUser.role}
            aria-invalid={Boolean(createUserErrors.role)}
            aria-describedby={createUserErrors.role ? 'create-user-role-error' : undefined}
            onChange={(event) => {
              setCreateUser((value) => ({ ...value, role: event.target.value }));
              setCreateUserErrors((value) => ({ ...value, role: undefined, form: undefined }));
            }}>
            <option value="" disabled>Select a role</option>
            {(adminData?.roles ?? []).map((role) => <option key={role.name} value={role.name}
              disabled={role.name === 'Super Administrator' && !me.roles.includes('Super Administrator')}>
              {role.name}
            </option>)}
          </select>
          {createUserErrors.role && <p id="create-user-role-error" className="field-error">{createUserErrors.role}</p>}
        </form>
      </Sheet>

      {/* Person sheets. §13 §14 */}
      <Sheet
        open={callLogOpen}
        title="Log call"
        onClose={() => setCallLogOpen(false)}
        actions={
          <>
            <button className="btn btn-secondary flex-1"
                    onClick={() => setCallLogOpen(false)}>Cancel</button>
            <button
              className="btn btn-primary flex-1"
              disabled={!callLog.outcome}
              onClick={() => void withAutoSave(async () => {
                await api.logCall(person!.id, callLog);
                setCallLogOpen(false);
                setCallLog(callLogDefaults());
                if (callLog.scheduleVisit && !person?.doNotVisit) setScheduleOpen(true);
                setToast('Call logged');
              })}
            >
              {callLog.scheduleVisit ? 'Save and schedule visit' : 'Save'}
            </button>
          </>
        }
      >
        <CallLogBody state={callLog} onChange={setCallLog} />
      </Sheet>

      <Sheet
        open={scheduleOpen}
        title="Schedule visit"
        onClose={() => setScheduleOpen(false)}
        actions={
          <>
            <button className="btn btn-secondary flex-1"
                    onClick={() => setScheduleOpen(false)}>Cancel</button>
            <button
              className="btn btn-primary flex-1"
              disabled={!schedule.date}
              onClick={() => void withAutoSave(async () => {
                await api.scheduleVisit(person!.id, schedule);
                setScheduleOpen(false);
                setVisitsRetry((attempt) => attempt + 1);
                setToast('Visit scheduled');
              })}
            >
              Schedule
            </button>
          </>
        }
      >
        <ScheduleVisitBody state={schedule} teams={teamOptions} onChange={setSchedule} />
      </Sheet>

      <Sheet
        open={noteOpen}
        title="Add note"
        onClose={() => setNoteOpen(false)}
        actions={<>
          <button className="btn btn-secondary flex-1" onClick={() => setNoteOpen(false)}>Cancel</button>
          <button className="btn btn-primary flex-1" disabled={!noteText.trim()}
            onClick={() => void withAutoSave(async () => {
              if (!person) return;
              await api.addNote(person.id, noteText.trim());
              setPerson(await api.person(person.id));
              setNoteOpen(false);
              setNoteText('');
              setToast('Note added');
            })}>Save</button>
        </>}
      >
        <label className="field"><span>Note</span><textarea className="input min-h-[120px]"
          value={noteText} onChange={(event) => setNoteText(event.target.value)} maxLength={4000} /></label>
      </Sheet>

      <Sheet
        open={avAddressOpen}
        title="Submit address for AV review"
        onClose={() => setAvAddressOpen(false)}
        actions={<>
          <button className="btn btn-secondary flex-1" onClick={() => setAvAddressOpen(false)}>Cancel</button>
          <button className="btn btn-primary flex-1" disabled={avAddress.trim().length < 8}
            onClick={() => void withAutoSave(async () => {
              if (!person) return;
              const result = await api.submitAvAddress(person.id, avAddress.trim(), avNotes.trim(),
                selectedCaseId || person.caseRecordId || undefined);
              setAvAddressOpen(false);
              setToast(`Address sent for AV review. ${result.notified} reviewer${result.notified === 1 ? '' : 's'} notified.`);
            })}>Submit</button>
        </>}
      >
        <p className="mb-3">The new location will not enter a field route until AV validates it.</p>
        <label className="field"><span>Case requiring address review</span><select className="input"
          value={selectedCaseId || person?.caseRecordId || ''}
          onChange={(event) => setSelectedCaseId(event.target.value)}>
          {(person?.cases ?? []).map((item) => <option key={item.id} value={item.id}>
            {item.caseNumber} · {item.status}
          </option>)}
        </select></label>
        <label className="field"><span>New address</span><textarea className="input min-h-[100px]"
          value={avAddress} onChange={(event) => setAvAddress(event.target.value)} maxLength={500} /></label>
        <label className="field mt-3"><span>Source and supporting notes</span><textarea className="input min-h-[100px]"
          value={avNotes} onChange={(event) => setAvNotes(event.target.value)} maxLength={4000} /></label>
      </Sheet>

      <Sheet
        open={reassignOpen}
        title="Reassign case"
        onClose={() => setReassignOpen(false)}
        actions={<>
          <button className="btn btn-secondary flex-1" onClick={() => setReassignOpen(false)}>Cancel</button>
          <button className="btn btn-primary flex-1" disabled={!reassignTeamId || !(selectedCaseId || person?.caseRecordId)}
            onClick={() => void withAutoSave(async () => {
              if (!person || !(selectedCaseId || person.caseRecordId)) return;
              const result = await api.reassignCase(selectedCaseId || person.caseRecordId!, reassignTeamId);
              if (result.error) throw new Error(result.error);
              setPerson(await api.person(person.id));
              setReassignOpen(false);
              setToast(result.routePlanId
                ? 'Case moved into the receiving team’s route.'
                : 'Case assigned to the receiving team for scheduling.');
            })}>Reassign</button>
        </>}
      >
        <p className="mb-3">The receiving route will be recalculated when a pending trip is available.</p>
        <label className="field"><span>Case to transfer</span><select className="input"
          value={selectedCaseId || person?.caseRecordId || ''}
          onChange={(event) => setSelectedCaseId(event.target.value)}>
          {(person?.cases ?? []).map((item) => <option key={item.id} value={item.id}>
            {item.caseNumber} · {item.status} · {item.teamName ?? 'Unassigned'}
          </option>)}
        </select></label>
        <label className="field"><span>Receiving team</span><select className="input"
          value={reassignTeamId} onChange={(event) => setReassignTeamId(event.target.value)}>
          <option value="">Select a team</option>
          {teamOptions.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
        </select></label>
      </Sheet>

      <Sheet
        open={adminUser !== null}
        title="User"
        onClose={() => setAdminUser(null)}
        actions={
          <button className="btn btn-secondary btn-block"
                  onClick={() => setAdminUser(null)}>Close</button>
        }
      >
        {adminUser && adminData && (
          <UserDetailBody
            user={adminUser}
            roles={adminData.roles}
            scopes={(Object.keys(SCOPE_LABELS) as ScopeKind[])
              .map((id) => ({ id, label: SCOPE_LABELS[id] }))}
            isSelf={adminUser.id === me.userId}
            canManageRoles={heldPermissions.has('roles.manage')}
            canSendInvitation={heldPermissions.has('users.update')}
            canManageAccount={heldPermissions.has('users.disable')}
            invitationSending={invitationSending}
            onRoleChange={(role) => void requestAdminChange({
              userId: adminUser.id, kind: 'role', value: role,
            })}
            onAvRoleChange={(enabled) => void withAutoSave(async () => {
              await withFreshAdminAccess(() => api.setUserAvRole(adminUser.id, enabled));
              const next = await api.admin('users');
              setAdminData(next);
              setAdminUser(next.users.find((user) => user.id === adminUser.id) ?? null);
              setToast(enabled ? 'AV reviewer access assigned.' : 'AV reviewer access removed.');
            })}
            onScopeChange={(scope) => void requestAdminChange({
              userId: adminUser.id, kind: 'scope', value: scope,
            })}
            onSendInvitation={() => {
              setInvitationSending(true);
              void api.resendInvitation(adminUser.id)
                .then(() => setToast('Invitation sent.'))
                .catch((error) => {
                  const message = error instanceof ApiError
                    ? error.body && typeof error.body === 'object'
                      ? String((error.body as { message?: string }).message ?? error.reason)
                      : error.reason
                    : String(error);
                  setToast(message || 'The invitation could not be delivered.');
                })
                .finally(() => setInvitationSending(false));
            }}
            onDisable={() => void withAutoSave(async () => {
              await api.disableUser(adminUser.id);
              setAdminUser(null);
              setToast('Account disabled');
            })}
            onUnlock={() => void withAutoSave(async () => {
              await api.unlockUser(adminUser.id);
              setAdminData(await api.admin('users'));
              setAdminUser(null);
              setToast('Account unlocked');
            })}
          />
        )}
      </Sheet>

      <Sheet
        open={adminStepUpOpen}
        title="Security verification required"
        onClose={() => {
          if (adminStepUpBusy) return;
          setAdminStepUpOpen(false);
          setPendingAdminChange(null);
          setAdminStepUpOptions(null);
          setAdminStepUpError(null);
        }}
        actions={
          <>
            <button
              className="btn btn-secondary flex-1"
              disabled={adminStepUpBusy}
              onClick={() => {
                setAdminStepUpOpen(false);
                setPendingAdminChange(null);
                setAdminStepUpOptions(null);
                setAdminStepUpError(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary flex-1"
              disabled={adminStepUpBusy || !adminStepUpOptions}
              onClick={() => void completeAdminStepUp()}
            >
              {adminStepUpBusy ? 'Verifying…' : 'Verify with security key'}
            </button>
          </>
        }
      >
        <p className="text-base mb-3">
          Changing a user’s role or data scope requires a fresh security-key check.
          Your current session will remain signed in.
        </p>
        {adminStepUpError && (
          <p role="alert" className="form-error">{adminStepUpError}</p>
        )}
      </Sheet>

      <Sheet
        open={outcomeOpen}
        title="Log outcome"
        onClose={() => setOutcomeOpen(false)}
        actions={
          <>
            <button className="btn btn-secondary flex-1"
                    onClick={() => setOutcomeOpen(false)}>
              Cancel
            </button>
            <button className="btn btn-primary flex-1"
                    disabled={!outcome}
                    onClick={() => void submitOutcome()}>
              Complete
            </button>
          </>
        }
      >
        <FieldOutcomeBody
          outcome={outcome}
          note={outcomeNote}
          recordCount={fieldStop?.records.filter((r) => r.selected).length ?? 0}
          onOutcomeChange={setOutcome}
          onNoteChange={setOutcomeNote}
        />
      </Sheet>

      <Sheet
        open={locationEditor !== null}
        title={locationEditor?.mode === 'place' ? 'Place on map' : 'Edit address'}
        onClose={() => setLocationEditor(null)}
        actions={<>
          <button className="btn btn-secondary flex-1" onClick={() => setLocationEditor(null)}>Cancel</button>
          <button className="btn btn-primary flex-1"
            disabled={!locationEditor?.address.trim() || (locationEditor?.mode === 'place'
              && (!Number.isFinite(Number(locationEditor?.lat)) || !Number.isFinite(Number(locationEditor?.lng))))}
            onClick={() => void withAutoSave(async () => {
              if (!locationEditor) return;
              await api.updateLocation(locationEditor.id, {
                address: locationEditor.address.trim(), city: locationEditor.city.trim(),
                state: locationEditor.state.trim(), zip: locationEditor.zip.trim(),
                ...(locationEditor.mode === 'place'
                  ? { lat: Number(locationEditor.lat), lng: Number(locationEditor.lng) }
                  : {}),
              });
              const result = await api.unresolvedLocations();
              setUnresolved(result.items);
              setToast(locationEditor.mode === 'place' ? 'Location placed' : 'Corrected address saved');
              setLocationEditor(null);
            })}>Save</button>
        </>}
      >
        {locationEditor && <div>
          <label className="field"><span>Street address</span><input className="input" value={locationEditor.address}
            onChange={(event) => setLocationEditor({ ...locationEditor, address: event.target.value })} /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="field"><span>City</span><input className="input" value={locationEditor.city}
              onChange={(event) => setLocationEditor({ ...locationEditor, city: event.target.value })} /></label>
            <label className="field"><span>State</span><input className="input" value={locationEditor.state}
              onChange={(event) => setLocationEditor({ ...locationEditor, state: event.target.value })} /></label>
          </div>
          <label className="field"><span>ZIP</span><input className="input" value={locationEditor.zip}
            onChange={(event) => setLocationEditor({ ...locationEditor, zip: event.target.value })} /></label>
          {locationEditor.mode === 'place' && <div className="grid grid-cols-2 gap-2">
            <label className="field"><span>Latitude</span><input className="input" inputMode="decimal" value={locationEditor.lat}
              onChange={(event) => setLocationEditor({ ...locationEditor, lat: event.target.value })} /></label>
            <label className="field"><span>Longitude</span><input className="input" inputMode="decimal" value={locationEditor.lng}
              onChange={(event) => setLocationEditor({ ...locationEditor, lng: event.target.value })} /></label>
          </div>}
          <p className="text-sm text-neutral-700">The imported address remains preserved. Corrections are marked as user supplied.</p>
        </div>}
      </Sheet>

      <Sheet
        open={analysisOpen}
        title="Route analysis"
        onClose={() => setAnalysisOpen(false)}
        actions={<button className="btn btn-secondary btn-block" onClick={() => setAnalysisOpen(false)}>Close</button>}
      >
        {review && <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3">
            <div><dt className="text-h6 uppercase text-neutral-700">Road miles</dt><dd className="font-heading text-h4 font-semibold">{review.roadMiles}</dd></div>
            <div><dt className="text-h6 uppercase text-neutral-700">Longest day</dt><dd className="font-heading text-h4 font-semibold">{review.longestTeamDayLabel}</dd></div>
            <div><dt className="text-h6 uppercase text-neutral-700">Assignment</dt><dd className="font-heading text-h4 font-semibold">{review.assignmentStatus}</dd></div>
            <div><dt className="text-h6 uppercase text-neutral-700">Conflicts</dt><dd className="font-heading text-h4 font-semibold">{review.scheduleConflicts}</dd></div>
          </dl>
          <ul>{review.teams.map((team) => <li key={team.teamId} className="border-t border-neutral-300 py-3">
            <p className="font-heading font-semibold">{team.teamName}</p>
            <p className="text-sm text-neutral-700">{team.stopCount} stops · {team.visitCount} visits · {team.miles} mi · {team.windowLabel}</p>
            {team.warning && <p className="mt-1 text-sm text-alert-700">{team.warning}</p>}
          </li>)}</ul>
          <p className="text-sm text-neutral-700">{review.estimated ? 'Estimated routing data is clearly marked because the road service was unavailable.' : 'Distances and travel times came from the configured road network.'}</p>
        </div>}
      </Sheet>

      <Toast message={toast} onDone={() => setToast(null)} />
    </AppShell>
  );
}

function shiftCalendarDate(value: string, view: CalendarView, direction: -1 | 1): string {
  const date = new Date(`${value}T12:00:00Z`);
  if (view === 'Day') date.setUTCDate(date.getUTCDate() + direction);
  else if (view === 'Week') date.setUTCDate(date.getUTCDate() + 7 * direction);
  else if (view === 'Month' || view === 'Agenda') date.setUTCMonth(date.getUTCMonth() + direction);
  else date.setUTCFullYear(date.getUTCFullYear() + direction);
  return date.toISOString().slice(0, 10);
}

function dateKeyInTimeZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

async function shareTrip(route: TeamRouteData | null): Promise<void> {
  if (!route) return;
  const share = {
    title: `${route.teams.find((t) => t.id === route.teamId)?.name ?? 'Team'} route`,
    text: `${route.dateLabel}: ${route.stopCount} stops`,
    url: window.location.href,
  };
  if (navigator.share) await navigator.share(share);
  else {
    await navigator.clipboard.writeText(`${share.title}\n${share.text}\n${share.url}`);
  }
}

function downloadIcs(contents: string, dateLabel: string): void {
  const blob = new Blob([contents], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `veritas-route-${dateLabel.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** §5 authentication surfaces. The server decides which stage applies. */
function unusedSignInPrompt() {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-[420px]">
        <h1 className="mb-2">Veritas</h1>
        <p className="text-base text-neutral-700 mb-6">
          Sign in with your organization account to continue.
        </p>
        {/* Redirects to the SAML start endpoint. No credentials are handled
            here — Google Workspace is the identity provider. §30 */}
        <a href="/auth/saml/start" className="btn btn-primary btn-field">
          Sign in with Google Workspace
        </a>
      </div>
    </div>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + (parts.at(-1)?.[0] ?? '')).toUpperCase();
}

function mobileActivationCopy(cause: unknown): string {
  const reason = cause instanceof Error ? cause.message : String(cause);
  if (reason === 'mobile_credentials_required') return 'Enter your work username and password.';
  if (reason === 'invalid_credentials') return 'The username, password, or authenticator code was not accepted.';
  if (reason === 'mobile_mfa_failed') return 'That authenticator code was not accepted. Try the next current code.';
  if (reason === 'mobile_mfa_locked') return 'Verification is temporarily locked after repeated failures. Wait 15 minutes.';
  if (reason === 'totp_not_enrolled') return 'This account does not have an authenticator enrolled. Contact a VERITAS administrator.';
  if (reason === 'mobile_certificate_rejected') return 'This certificate is not provisioned, was revoked, or belongs to a disabled account.';
  if (reason === 'mobile_totp_format_invalid') return 'Enter the current six-digit authenticator code.';
  if (/device.*secure|passcode/iu.test(reason)) {
    return 'Set a screen lock on this phone, then try activation again.';
  }
  if (/protect.*mobile keys|keystore|key package/iu.test(reason)) {
    return 'VERITAS could not protect this phone\'s security keys. Restart the app and try again.';
  }
  if (/identity|certificate|required/iu.test(reason)) {
    return 'VERITAS could not create this phone\'s protected identity. Restart the app and try again.';
  }
  return 'Secure activation could not be completed. Check the network and try again.';
}

function takeSetupTokenFromLocation(): string | null {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const token = params.get('setup');
  if (token !== null) {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }
  return token && /^[A-Za-z0-9_-]{43}$/.test(token) ? token : null;
}

export function validateCreateUser(
  values: CreateUserValues,
  allowedRoles: string[],
): CreateUserErrors {
  const errors: CreateUserErrors = {};
  const displayName = values.displayName.trim();
  const email = values.email.trim();

  if (!displayName) errors.displayName = 'Enter the user’s display name.';
  else if (displayName.length > 120) errors.displayName = 'Display name must be 120 characters or fewer.';

  if (!email) errors.email = 'Enter the user’s email address.';
  else if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Enter a valid email address.';
  }

  if (!values.role) errors.role = 'Select a role.';
  else if (!allowedRoles.includes(values.role)) errors.role = 'Select an available role.';
  return errors;
}
